import { ExhFbcSettingsData, ExhSettingsDocument, ExhWbcSettingsData } from "types/measurement";

import { HospitalConfiguration } from "../../../components/pages/settings/context/types";

type Range = HospitalConfiguration["wbcRange"];

const toRange = (range?: { min?: number; max?: number }): Range | undefined => {
  if (!range || range.min === undefined || range.max === undefined) {
    return undefined;
  }
  return { min: range.min, max: range.max };
};

/**
 * Convert a `measurement-wbc-settings` document into the partial hospital
 * configuration shape. Fields that are missing from the document are left
 * undefined so callers can merge over their defaults.
 */
export const exhWbcSettingsConverter = (
  doc: ExhSettingsDocument<ExhWbcSettingsData> | undefined
): Partial<HospitalConfiguration> => {
  const data = doc?.data;
  if (!data) {
    return {};
  }

  const result: Partial<HospitalConfiguration> = {};
  const wbc = toRange(data.white_blood_cells_range);
  if (wbc) {
    result.wbcRange = wbc;
  }
  const granulocytes = toRange(data.granulocytes_range);
  if (granulocytes) {
    result.granulocyteRange = granulocytes;
  }
  if (typeof data.granulocytes_threshold === "number") {
    result.confirmatoryThreshold = data.granulocytes_threshold;
  }
  return result;
};

/**
 * Convert a `measurement-fbc-settings` document into the partial hospital
 * configuration shape.
 */
export const exhFbcSettingsConverter = (
  doc: ExhSettingsDocument<ExhFbcSettingsData> | undefined
): Partial<HospitalConfiguration> => {
  const data = doc?.data;
  if (!data) {
    return {};
  }

  const result: Partial<HospitalConfiguration> = {};
  const wbc = toRange(data.white_blood_cells_range);
  if (wbc) {
    result.wbcRange = wbc;
  }
  const neutrophils = toRange(data.neutrophils_range);
  if (neutrophils) {
    result.neutrophilRange = neutrophils;
  }
  const platelets = toRange(data.platelets_range);
  if (platelets) {
    result.plateletRange = platelets;
  }

  const hb = data.haemoglobin_range;
  if (hb) {
    if (hb.male_min !== undefined && hb.male_max !== undefined) {
      result.haemoglobinRangeMale = { min: hb.male_min, max: hb.male_max };
    }
    if (hb.female_min !== undefined && hb.female_max !== undefined) {
      result.haemoglobinRangeFemale = { min: hb.female_min, max: hb.female_max };
    }
  }

  if (typeof data.neutrophils_threshold === "number") {
    result.confirmatoryThreshold = data.neutrophils_threshold;
  }
  return result;
};

/* eslint-disable camelcase */
/**
 * Convert a hospital configuration into the `measurement-wbc-settings`
 * document data shape for writing back to ExtraHorizon.
 */
export const hospitalConfigToWbcSettingsData = (config: HospitalConfiguration): ExhWbcSettingsData => {
  const data: ExhWbcSettingsData = {
    white_blood_cells_range: { min: config.wbcRange.min, max: config.wbcRange.max },
    granulocytes_threshold: config.confirmatoryThreshold,
  };
  if (config.granulocyteRange) {
    data.granulocytes_range = { min: config.granulocyteRange.min, max: config.granulocyteRange.max };
  }
  return data;
};

/**
 * Convert a hospital configuration into the `measurement-fbc-settings`
 * document data shape for writing back to ExtraHorizon.
 */
export const hospitalConfigToFbcSettingsData = (config: HospitalConfiguration): ExhFbcSettingsData => {
  return {
    white_blood_cells_range: { min: config.wbcRange.min, max: config.wbcRange.max },
    neutrophils_range: { min: config.neutrophilRange.min, max: config.neutrophilRange.max },
    platelets_range: { min: config.plateletRange.min, max: config.plateletRange.max },
    haemoglobin_range: {
      male_min: config.haemoglobinRangeMale.min,
      male_max: config.haemoglobinRangeMale.max,
      female_min: config.haemoglobinRangeFemale.min,
      female_max: config.haemoglobinRangeFemale.max,
    },
    neutrophils_threshold: config.confirmatoryThreshold,
  };
};

/**
 * Map ExtraHorizon settings document field paths (snake_case, dotted) to the
 * matching Formik field paths used by the hospital configuration form.
 * Range sub-fields (`*.min` / `*.max`) collapse to the parent range field,
 * because `RangeField` renders a single error message for both inputs and
 * Formik would otherwise store an object under the parent key (which React
 * cannot render as a child).
 */
const exhToUiFieldPathMap: Record<string, string> = {
  "white_blood_cells_range.min": "wbcRange",
  "white_blood_cells_range.max": "wbcRange",
  "granulocytes_range.min": "granulocyteRange",
  "granulocytes_range.max": "granulocyteRange",
  granulocytes_threshold: "confirmatoryThreshold",
  "neutrophils_range.min": "neutrophilRange",
  "neutrophils_range.max": "neutrophilRange",
  "platelets_range.min": "plateletRange",
  "platelets_range.max": "plateletRange",
  "haemoglobin_range.male_min": "haemoglobinRangeMale",
  "haemoglobin_range.male_max": "haemoglobinRangeMale",
  "haemoglobin_range.female_min": "haemoglobinRangeFemale",
  "haemoglobin_range.female_max": "haemoglobinRangeFemale",
  neutrophils_threshold: "confirmatoryThreshold",
};
/* eslint-enable camelcase */

export const settingsErrorFieldConverter = (exhFieldPath: string): string | null => {
  return exhToUiFieldPathMap[exhFieldPath] ?? null;
};
